// react-magnetic-di - temporary comment, for auto dependency injection in tests
import React, { useEffect, useMemo } from 'react';

import AtlaskitPlaceholder from '@atlaskit/react-ufo/placeholder';
import { parseExtensionId } from '@atlassian/forge-ui/utils/parseExtensionId';

import type {
	ForgeExtension,
	UiModificationsConfigSingleApp,
} from '../../../../../common/types/apps';
import { useUIModificationsConsentStatusForApp } from '../../../../../controllers/adjustments-context/hooks/use-consent-status';
import { UIM_CONSENT_REQUIRED } from '../../../../../controllers/adjustments-context/types';
import { useExecutionContext } from '../../../../../controllers/execution-context';
import { useViewType } from '../../../../../controllers/issue-adjustments/hooks/use-view-type';
import { useIssueAdjustmentsActions } from '../../../../../controllers/issue-adjustments/main';
import { getAnalytics } from '../../../../../controllers/view-configuration';

import type { ForgeAppRendererComponent } from './types';
import { useCustomBridgeMethods } from './use-custom-bridge-methods';
import { useIframeTimeout } from './use-iframe-timeout';
import { useScopeChecks } from './use-scope-checks';
import { useThreeLOPromptProps } from './use-three-lo-prompt-props';
import { buildExtensionData } from './utils/build-extension-data';
import type { ExtensionData } from './utils/build-extension-data/types';

export interface SingleAppRendererProps {
	forgeExtension: ForgeExtension;
	uiModificationsConfig: UiModificationsConfigSingleApp;
	ForgeAppRenderer: ForgeAppRendererComponent;
}

export const SingleAppRenderer = ({
	forgeExtension,
	uiModificationsConfig,
	ForgeAppRenderer,
}: SingleAppRendererProps): React.JSX.Element | null => {
	const { scopes: moduleScopes, id: moduleId } = forgeExtension;
	const { setError } = useIssueAdjustmentsActions();
	const viewType = useViewType();

	const executionContext = useExecutionContext();
	const extensionData = useMemo<ExtensionData>(
		() => buildExtensionData(executionContext),
		[executionContext],
	);

	const { appId } = parseExtensionId(moduleId);

	// This hook can be removed and replaced with `onTimeout` from LazyIframe once
	// https://ecosystem.atlassian.net/browse/BUILD-1617 is done. For now it approximates an
	// iframe timeout if the iframe doesn't use the custom bridge method needed by @forge/jira-bridge
	const { setLoadingComplete } = useIframeTimeout({ forgeExtension });
	const { customBridgeMethods } = useCustomBridgeMethods({
		onBridgeCommunicationReceived: setLoadingComplete,
		uiModifications: uiModificationsConfig,
		moduleScopes,
		appId,
	});
	const requiredScopesFound = useScopeChecks(moduleScopes);
	const currentUIModificationsConsentStatus = useUIModificationsConsentStatusForApp({ appId });

	useEffect(() => {
		if (!requiredScopesFound) {
			setError({
				errorCode: 'SCOPES_MISSING_ERROR_CODE',
			});
			setLoadingComplete();
			getAnalytics(viewType).cancelTTI();
		}
	}, [requiredScopesFound, setError, setLoadingComplete, viewType]);

	useEffect(() => {
		if (currentUIModificationsConsentStatus === UIM_CONSENT_REQUIRED) {
			setLoadingComplete();
			getAnalytics(viewType).cancelTTI();
		}
	}, [currentUIModificationsConsentStatus, setLoadingComplete, viewType]);

	const consentProps = useThreeLOPromptProps({ forgeExtension });

	if (!requiredScopesFound) {
		return null;
	}

	return (
		<AtlaskitPlaceholder name="single-frame-iframe" fallback={null} key={moduleId}>
			<ForgeAppRenderer
				extension={forgeExtension}
				extensionData={extensionData}
				customBridgeMethods={customBridgeMethods}
				onIframeLoad={getAnalytics(viewType).markIframeLoad}
				isIframeResizable={false}
				isIframeHidden
				{...consentProps}
			/>
		</AtlaskitPlaceholder>
	);
};
